import React, { useMemo, useState } from 'react';
import { Box, ButtonV2, Flex, TextV2, Toggle, TooltipV2, useDisclosure, useToast } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Info, JoyAiV2, WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import type { EventVenue } from '@shared/utils/eventPlace';
import { useEventPlaceActions } from '@shared/utils/eventPlace';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useDeleteDialog } from '@shared/components/DeleteDialog/DeleteDialog.context';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';

import type { EventVenueState } from '@website-editor/customComponents/Accommodations/components/EventVenueDialog/EventVenueDialog.types';
import { useEventVenueDialogTelemetry } from '@website-editor/customComponents/Accommodations/components/EventVenueDialog/EventVenueDialog.telemetry';
import { useAdminAccommodationsTelemetry } from '@website-editor/customComponents/Accommodations/Accommodations.telemetry';
import { TravelMapBlockType } from '@website-editor/customComponents/Accommodations/Accommodations.types';
import { useAccommodationsDelete } from '@website-editor/customComponents/Accommodations/hooks/useAccommodationsDelete';

import type { AdvancedLocationInputV2FormFields } from '@shared/components/LocationInputV2';
import { LocationInputV2Expandable } from '@shared/components/LocationInputV2';
import { withFormikLocationInputV2 } from '@shared/components/LocationInputV2';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useUpdateWebsitePreview } from '@apps/admin/routes/WebsiteEditor/hooks/useUpdateWebsitePreview';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { useUpdateEventInfoSettingsMutation } from '../../../../../Settings/Settings.graphql.generated';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { formatLocationLabel } from '@shared/utils/locationFormatting';
import { isValidCoordinates } from '@shared/core/places/location';
import { addError } from '@shared/utils/logger';

const SEARCH_INPUT_INTENT = 'aiBorder';

const LocationInputV2ExpandableWithFormik = withFormikLocationInputV2(LocationInputV2Expandable);

interface FormProps {
  eventVenueState: EventVenueState;
  eventHandle?: string;
  venueInfo?: EventVenue;
  onError: () => void;
  onSaveVenue: (location?: AdvancedLocationInputV2FormFields) => void;
  pageSlug?: string;
  isSuperAdmin?: boolean;
  handleDialogDismiss: () => void;
  onChangeVenue: (location?: AdvancedLocationInputV2FormFields) => void;
}

export const Form = ({ eventVenueState, eventHandle, venueInfo, onSaveVenue, onError, pageSlug, isSuperAdmin, handleDialogDismiss, onChangeVenue }: FormProps) => {
  const [location, setLocation] = useState<AdvancedLocationInputV2FormFields>();
  const hasValidVenue = isValidCoordinates(location) && location.placeId && venueInfo?.googlePlaceId !== location.placeId;

  const isMobile = useIsMobileScreen();
  const { isOpen: updateEventLocation, onToggle: toggleUpdateEventLocation } = useDisclosure({ defaultIsOpen: true });

  const [isVenueLocationSyncToggleEnabled] = useIsFeatureEnabled('venueLocationSyncToggle');

  const { tq } = useTranslation('travelMap');

  const placeholders = useMemo(
    () => [
      tq`San Francisco City Hall`,
      tq`Martoca Beach Garden, Mexico`,
      tq`Bathers Pavilion, Balmoral Beach, Sydney`,
      tq`Heritage Hall, Vancouver, Canada`,
      tq`Amadeus, Maida Vale, London`,
      tq`Garfield Park Conservatory, Chicago`,
      tq`The Grove, Houston`,
      tq`The Plaza Hotel, New York City`,
    ],
    [tq],
  );

  const { updateWebsitePreview } = useUpdateWebsitePreview();

  const venueFormTrans = (() => {
    switch (eventVenueState) {
      case 'update':
        return {
          title: tq`Update your venue`,
          message: tq`Changing your venue location will update wherever it’s used. I.e. if you’ve already added hotels, distance and driving directions will be updated to this new location.`,
          cta: tq`Update Venue`,
          searchLabel: tq`Event Venue`,
          saveVenueError: tq`Please enter event venue.`,
        };
      default:
        return {
          title: tq`Add your venue`,
          message: tq`We’ll use this to personalize your guest website experience with maps, directions, things-to-do, nearby hotel picks and more.`,
          cta: tq`Save Venue`,
          searchLabel: tq`Event Venue or Destination`,
          saveVenueError: tq`Please enter event venue.`,
        };
    }
  })();

  const telemetry = useEventVenueDialogTelemetry();
  const websiteEditorTelemetry = useAdminAccommodationsTelemetry();

  const { openDialog } = useDeleteDialog();
  const { eventInfo } = useEventInfo();
  const { toast } = useToast();

  const handleOnDelete = () => {
    websiteEditorTelemetry.deleteItemSubmit(pageSlug || '', { itemType: TravelMapBlockType.travelMapVenue, isSuperAdmin: !!isSuperAdmin });
  };

  const { onDelete } = useAccommodationsDelete({ handleCloseOnSuccess: handleOnDelete });
  const [updateEventInfo] = useUpdateEventInfoSettingsMutation();

  const handleDeleteVenue = useEventCallback(() => {
    websiteEditorTelemetry.deleteItemDialog(pageSlug || '', { itemType: TravelMapBlockType.travelMapVenue });
    if (venueInfo) {
      openDialog({
        title: tq`Are you sure you want to remove the venue?`,
        message: tq`This cannot be undone.`,
        btnConfirm: tq`Remove Venue`,
        id: venueInfo?.id ?? undefined,
        btnCancel: tq`Cancel`,
        onConfirm: async () => {
          await onDelete(TravelMapBlockType.travelMapVenue, venueInfo?.id, venueInfo?.pointOnMapId);
          onChangeVenue(undefined);
          handleDialogDismiss();
        },
      });
    }
  });

  const { payload } = useRemoteFeatureFlag('venueAddressLocationEnabled');
  const placePredictionTypes = useMemo(() => (Object(payload)?.types as string[]) || [], [payload]);

  const onCompleted = useEventCallback(() => {
    if (location?.latitude && location.longitude) {
      onSaveVenue(location);
    }
  });
  const { createEventPlace, updateEventPlace, isLoading } = useEventPlaceActions({ onCompleted, onError, updateWebsitePreview });
  const { map } = useJoyInteractableMapContext();
  const { centerAndZoom } = useGoogleInteractableMap();

  const handleOnSaveVenue = useEventCallback(async () => {
    if (location && map) {
      centerAndZoom?.({ lat: location.latitude || 0, lng: location.longitude || 0 }, 5, map);
    }

    if (isValidCoordinates(location) && location.placeId) {
      switch (eventVenueState) {
        case 'create':
          telemetry.createSaveEventVenue({ name: location.locationValue, latitude: location.latitude, longitude: location.longitude }, eventHandle);
          createEventPlace({ payload: { googlePlaceId: location.placeId } });
          break;
        case 'update':
          telemetry.updateEventVenue({ name: location.locationValue, latitude: location.latitude, longitude: location.longitude }, eventHandle);
          updateEventPlace({ payload: { id: venueInfo?.id, pointOnMapId: venueInfo?.pointOnMapId, googlePlaceId: location.placeId } });
          break;
      }
      if (updateEventLocation) {
        try {
          await updateEventInfo({
            variables: {
              eventId: eventInfo?.eventId || '',
              payload: {
                location: {
                  latitude: location.latitude,
                  longitude: location.longitude,
                  name: formatLocationLabel(location) || location.locationValue || location.displayName,
                  placeId: location.placeId,
                },
              },
            },
          });
        } catch (error) {
          addError('Failed to update event location', { context: 'VenueForm' });
          toast(tq`Failed to update event location`, { icon: <WarningTriangle /> });
        }
      }
    } else {
      addError('Invalid venue location', { context: 'VenueForm' });
      toast(tq`Please select a venue from the suggestions`, { icon: <WarningTriangle /> });
    }
  });

  const handleOnChangeLocation = useEventCallback((values: AdvancedLocationInputV2FormFields) => {
    if (isValidCoordinates(values) && values.placeId) {
      setLocation(values);
      onChangeVenue(values);
    }
  });

  // Create location bias from event location
  const locationBias = useMemo(() => {
    const eventLocationInfo = eventInfo?.locationInfo;

    if (eventLocationInfo?.latitude && eventLocationInfo?.longitude) {
      const bias = {
        latitude: eventLocationInfo.latitude,
        longitude: eventLocationInfo.longitude,
        radius: 100000, // 100km radius in meters
      };
      return bias;
    }
    return undefined;
  }, [eventInfo?.locationInfo]);

  const renderCta = useMemo(() => {
    return (
      <Flex
        width="100%"
        maxWidth={{ _: '100%', sm2: pxToRem(392) }}
        flexDirection="column"
        justifyContent="center"
        borderTop={{ _: '1px solid', sm2: 'none' }}
        borderTopColor="mono3"
        paddingX={{ _: 6, sm2: 0 }}
        paddingY={{ _: 5, sm2: 0 }}
        position={{ _: 'sticky', sm2: 'relative' }}
        backgroundColor="white"
        bottom={0}
        zIndex="sticky"
      >
        <ButtonV2
          typographyVariant="button2"
          intent="neutral"
          textAlign="center"
          shape="rounded"
          onClick={handleOnSaveVenue}
          disabled={!hasValidVenue}
          loading={isLoading}
          fullWidth
        >
          {venueFormTrans.cta}
        </ButtonV2>
        {!isMobile && eventVenueState === 'create' && (
          <ButtonV2
            typographyVariant="button2"
            intent="neutral"
            variant="ghost"
            textAlign="center"
            onClick={handleDialogDismiss}
          >
            {tq`I don’t have a venue yet`}
          </ButtonV2>
        )}
        {/* Only Super Admins should see button to remove the venue */}
        {eventVenueState === 'update' && isSuperAdmin && (
          <Box marginTop={6}>
            <TextV2
              typographyVariant="label3"
              color="newUITest1"
              textTransform="uppercase"
              marginBottom={4}
              textAlign="center"
            >
              {tq`Super Admins Only`}
            </TextV2>
            <ButtonV2
              fullWidth
              typographyVariant="button2"
              intent={'neutral'}
              variant={'outline'}
              color="mono14"
              textAlign="center"
              shape="rounded"
              onClick={handleDeleteVenue}
            >
              {tq`Remove Venue`}
            </ButtonV2>
          </Box>
        )}
      </Flex>
    );
  }, [eventVenueState, handleDeleteVenue, handleOnSaveVenue, hasValidVenue, isLoading, isSuperAdmin, tq, venueFormTrans.cta, handleDialogDismiss, isMobile]);

  return (
    <Flex
      flex={1}
      flexDirection="column"
      gap={7}
      alignItems="center"
      justifyContent="center"
    >
      <Flex
        flexDirection="column"
        gap={5}
        alignItems="center"
        paddingX={{ _: 6, sm2: 0 }}
        paddingTop={{ _: 6, sm2: 0 }}
      >
        <JoyAiV2 size={64} />
        <Box
          typographyVariant="hed6"
          fontSize={pxToRem(26)}
          textAlign="center"
        >
          {venueFormTrans.title}
        </Box>
        <Box
          typographyVariant="body3"
          textAlign="center"
          maxWidth={{ _: '100%', sm2: pxToRem(362) }}
          fontSize={pxToRem(16)}
          color="mono12"
        >
          {venueFormTrans.message}
        </Box>
      </Flex>
      <Flex
        width="100%"
        flexDirection="column"
        textAlign="left"
        paddingX={{ _: 6, sm2: 0 }}
        maxWidth={{ _: '100%', sm2: pxToRem(392) }}
        justifyContent="center"
      >
        <Formik<{
          eventVenue: AdvancedLocationInputV2FormFields | null;
        }>
          initialValues={{
            eventVenue: {
              placeId: venueInfo?.googlePlaceId ?? '',
              locationValue: venueInfo?.name ?? '',
              latitude: venueInfo?.latitude,
              longitude: venueInfo?.longitude,
              city: venueInfo?.city || undefined,
              stateProvince: venueInfo?.stateProvince || undefined,
              country: venueInfo?.country || undefined,
            },
          }}
          validationSchema={Yup.object({
            eventVenue: Yup.object<AdvancedLocationInputV2FormFields>({
              locationValue: Yup.string().required(venueFormTrans.saveVenueError),
              latitude: Yup.number(),
              longitude: Yup.number(),
              placeId: Yup.string(),
              address: Yup.string(),
            })
              .nullable(true)
              .required(venueFormTrans.saveVenueError),
          })}
          onSubmit={() => {}}
        >
          {({ values }) => {
            const locationLabel = formatLocationLabel(values.eventVenue);
            const toggleText = locationLabel ? tq`Also update my event location to ${locationLabel}` : tq`Also update my event location to match the venue`;
            return (
              <>
                <LocationInputV2ExpandableWithFormik
                  name="eventVenue"
                  label={venueFormTrans.searchLabel}
                  placeholders={placeholders}
                  initialValues={{
                    placeId: venueInfo?.googlePlaceId ?? '',
                    locationValue: venueInfo?.name ?? '',
                    latitude: venueInfo?.latitude,
                    longitude: venueInfo?.longitude,
                    city: venueInfo?.city || undefined,
                    stateProvince: venueInfo?.stateProvince || undefined,
                    country: venueInfo?.country || undefined,
                  }}
                  onChangeLocation={handleOnChangeLocation}
                  placePredictionTypes={placePredictionTypes}
                  locationBias={locationBias}
                  intent={SEARCH_INPUT_INTENT}
                  allowCustomPlace={false}
                  shouldCloseOnSelect
                  isHideListItemIcon
                  expandedViewTitle={venueFormTrans.title}
                />

                {isVenueLocationSyncToggleEnabled && (
                  <Flex
                    flexDirection="row"
                    gap={3}
                    alignItems="center"
                    marginTop={5}
                  >
                    <Toggle
                      onChange={toggleUpdateEventLocation}
                      label=""
                      hideLabel={true}
                      checked={updateEventLocation}
                    />
                    <TextEllipsisWrapper lineClamp={2}>
                      <TextV2
                        typographyVariant="label2"
                        color="mono10"
                      >
                        {toggleText}
                      </TextV2>
                    </TextEllipsisWrapper>
                    <TooltipV2
                      content={<TextV2 typographyVariant="label2">{toggleText}</TextV2>}
                      onMouseEnterDelay={500}
                    >
                      <Info
                        size={16}
                        cursor="pointer"
                        color="mono10"
                      />
                    </TooltipV2>
                  </Flex>
                )}
              </>
            );
          }}
        </Formik>
      </Flex>
      {/* CTA shown when input is collapsed */}
      {renderCta}
    </Flex>
  );
};
